import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, DemoDataBanner, GeneralFeedbackButton } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { BreadcrumbPathProps } from '@frontend/design-system';
import { URLs } from '../../../../constants';
import { featureFlags } from '../../../../feature-flags';
import { MessagingCharts } from '../../components/messaging-roi/charts/messaging-charts';
import { MessagingMultiSubView } from '../../components/messaging-roi/messaging-multi-sub-view';
import { MessagingSingleSubView } from '../../components/messaging-roi/messaging-single-sub-view';
import { MessagingSubView } from '../../components/messaging-roi/messaging-sub-view';
import { customROITimePeriods } from '../../constants';
import { messagingOverviewFormatter } from '../../helpers/messaging-overview-formatter';
import { useFetchMessagingROIOOverview } from '../../hooks/use-fetch-messaging-roi-overview';
import { MessagingOverview } from '../../types';
import { getDateRangeText } from '../../utils/date-range-text';
import { BTRoiScheduleRateChart } from '../charts/bt-roi-schedule-rate-chart';
import { BTSummary } from '../components/bt-summary';
import { Filters } from '../components/filters';
import { HighlightBTValue } from '../components/highlight-bt-value';
import { useBTRoiShallowStore } from '../hooks/use-bt-roi-store';

export const BTRoiDashboard = () => {
  const { t } = useTranslation('analytics');
  const {
    definitionsHelperDateRange,
    filterHintText: subtitle,
    filters,
    setDefinitionsHelperDateRange,
  } = useBTRoiShallowStore('definitionsHelperDateRange', 'filterHintText', 'filters', 'setDefinitionsHelperDateRange');

  const search = useSearch({ strict: false }) ?? { view: 'leadsContacted' };
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.bulkTextRoiDemoData);
  const { averageProductionAmount } = AnalyticsHooks.useAverageProductionAmount();

  const subViewTitle = useMemo(() => {
    let title = '';

    switch (search.view) {
      case 'leadsContacted':
        title = t('Patients Contacted');
        break;

      case 'patientsScheduled':
        title = t('Patients Scheduled');
        break;

      case 'revenueGenerated':
        title = t('Production Value');
        break;
    }

    return title;
  }, [search.view]);

  const breadcrumbs: BreadcrumbPathProps[] = useMemo(() => {
    const values: BreadcrumbPathProps[] = [
      {
        label: t('Analytics'),
        to: URLs.ANALYTICS_DASHBOARD,
      },
      {
        label: t('Bulk Texting'),
        to: URLs.ROI_BULK_TEXT,
      },
    ];

    if (subViewTitle) {
      values.push({
        label: subViewTitle,
      });
    }

    return values;
  }, [subViewTitle]);

  const overviewFetchProps = useFetchMessagingROIOOverview({
    additionalRequestPayload: {
      roiType: RoiType.MARKETING_MESSAGE,
    },
    filters,
    isDemoAccount,
  });

  const { aggregated, isLoading } = overviewFetchProps;

  const overviewData: MessagingOverview = useMemo(() => {
    return messagingOverviewFormatter(aggregated);
  }, [aggregated]);

  useEffect(() => {
    setDefinitionsHelperDateRange(
      getDateRangeText({
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        timePeriods: customROITimePeriods,
      })
    );
  }, [filters?.startDate, filters?.endDate]);

  return (
    <>
      {isDemoAccount && (
        <DemoDataBanner title={t("You're taking Bulk Texting Conversion for a test drive with demo data!")} />
      )}
      <Page>
        <Page.Header>
          <Page.Header.Breadcrumbs breadcrumbs={breadcrumbs} />
          <Page.Header.Heading>
            <Page.Header.Title title={subViewTitle || t('Bulk Texting Conversion')} />
            <Page.Header.Subtitle subtitle={t('Showing results for {{subtitle}}', { subtitle })} />
            <Page.Header.Action>
              <GeneralFeedbackButton selectedLocationFromPageFilter={filters?.locationIds} />
            </Page.Header.Action>
          </Page.Header.Heading>
        </Page.Header>

        <Page.Body css={styles.body}>
          <div css={styles.body}>
            <Filters isLoading={isLoading} />

            {subViewTitle ? (
              <MessagingSubView filters={filters} isDemoAccount={isDemoAccount} roiType={RoiType.MARKETING_MESSAGE}>
                {(filters?.locationIds?.length || 0) > 1 ? (
                  <MessagingMultiSubView {...overviewFetchProps} filters={filters} overviewData={overviewData} />
                ) : (
                  <MessagingSingleSubView
                    dateRangeHelperText={definitionsHelperDateRange}
                    filters={filters}
                    isDemoAccount={isDemoAccount}
                    isLoading={isLoading}
                    overviewData={overviewData}
                    subViewUrl={URLs.ROI_BULK_TEXT}
                  />
                )}
              </MessagingSubView>
            ) : (
              <>
                <HighlightBTValue
                  value={overviewData.revenueGenerated.totalCompletedAppts * averageProductionAmount.value}
                />
                <BTSummary data={overviewData} isLoading={isLoading} />
                <MessagingCharts
                  data={overviewData}
                  dateRangeHelperText={definitionsHelperDateRange}
                  filteredLocationIds={filters?.locationIds ?? []}
                  isDemoAccount={isDemoAccount}
                  isLoading={isLoading}
                  subViewUrl={URLs.ROI_BULK_TEXT}
                />
                <BTRoiScheduleRateChart />
              </>
            )}
          </div>
        </Page.Body>
      </Page>
    </>
  );
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
};
